import type { SortingState } from '@tanstack/react-table';
import { useAuditColumns } from '../../hooks/audit/useAuditColumns';
import type { AuditDetail } from '../../stores/auditManagementStore';
import {
  selectAudit,
  setPage,
  setSorting,
} from '../../stores/auditManagementStore';
import { DataTable } from '../common/DataTable';
import { Pagination } from '../common/Pagination';

interface AuditTableProps {
  audits: AuditDetail[];
  isLoading: boolean;
  pagination: {
    currentPage: number;
    totalPages: number;
    total: number;
  };
  sorting: SortingState;
  selectedAuditId: string | null;
}

const formatDateTime = (date: string | number) => {
  return new Date(date).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const getActionColor = (action: string) => {
  switch (action.toLowerCase()) {
    case 'create':
      return 'text-green-400 bg-green-400/10 border-green-400/20';
    case 'update':
      return 'text-blue-400 bg-blue-400/10 border-blue-400/20';
    case 'delete':
      return 'text-red-400 bg-red-400/10 border-red-400/20';
    default:
      return 'text-neutral-400 bg-neutral-400/10 border-neutral-400/20';
  }
};

export const AuditTable = ({
  audits,
  isLoading,
  pagination,
  sorting,
  selectedAuditId,
}: AuditTableProps) => {
  const columns = useAuditColumns();

  return (
    <div className="md:bg-neutral-900 md:rounded-lg md:border md:border-neutral-700 overflow-hidden">
      <div className="px-6 py-4 border-b border-neutral-700 hidden md:flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h2 className="text-lg font-semibold text-white">Audit Logs</h2>
          <span className="text-sm text-neutral-300">
            {pagination.total.toLocaleString()} total records
          </span>
        </div>
        <div className="text-sm text-neutral-300">
          Page {pagination.currentPage} of {pagination.totalPages}
        </div>
      </div>

      <DataTable
        data={audits}
        columns={columns}
        isLoading={isLoading}
        emptyMessage="No audit records found"
        onRowClick={(row) => selectAudit(row.id)}
        selectedRowId={selectedAuditId || undefined}
        getRowId={(row) => row.id}
        sorting={sorting}
        onSortingChange={setSorting}
        enableSorting
        renderMobileItem={(audit) => (
          <div className="space-y-2">
            <div className="flex justify-between items-start">
              <span
                className={`px-2 py-0.5 rounded text-xs font-medium border uppercase tracking-wider ${getActionColor(
                  audit.action,
                )}`}
              >
                {audit.action}
              </span>
              <span className="text-xs text-neutral-500">
                {formatDateTime(audit.createdAt)}
              </span>
            </div>

            <div className="text-sm text-white font-medium">
              {audit.entityType}
            </div>

            <div className="flex justify-between items-end text-xs text-neutral-400">
              <div className="flex flex-col gap-0.5">
                <span>
                  By:{' '}
                  <span className="text-neutral-300">
                    {audit.user?.email || 'System'}
                  </span>
                </span>
                {audit.entityId && (
                  <span className="font-mono text-[10px] text-neutral-600">
                    {audit.entityId}
                  </span>
                )}
              </div>
            </div>
          </div>
        )}
      />

      <div className="border-t border-neutral-700">
        <Pagination
          currentPage={pagination.currentPage}
          totalPages={pagination.totalPages}
          onPageChange={setPage}
        />
      </div>
    </div>
  );
};
